<template>
    <div class="container-fluid">
        <div class="page-title">
            <div class="row">
                <div class="col-6">
                    <h3>KYT Chain Dashboard</h3>
                </div>
                <div class="col-6">
                    <ol class="breadcrumb">
                        <li class="breadcrumb-item">
                            <router-link to="/">Home</router-link>
                        </li>
                        <li class="breadcrumb-item active">KYT Chain Dashboard</li>
                    </ol>
                </div>
            </div>
        </div>
    </div>

    <div class="container-fluid">
        <KytChainLoading 
            v-if="loading" 
            message="Loading dashboard data..." 
            size="lg" 
            color="primary" 
        />

        <div v-else>
            <!-- Summary Cards -->
            <div class="row">
                <div class="col-md-3 col-sm-6">
                    <div class="card o-hidden">
                        <div class="bg-primary b-r-4 card-body">
                            <div class="media static-top-widget">
                                <div class="align-self-center text-center">
                                    <vue-feather type="database" class="font-primary"></vue-feather>
                                </div>
                                <div class="media-body">
                                    <span class="m-0">Total Logs</span>
                                    <h4 class="mb-0 counter">{{ summary?.totals?.logs || 0 }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-3 col-sm-6">
                    <div class="card o-hidden">
                        <div class="bg-success b-r-4 card-body">
                            <div class="media static-top-widget">
                                <div class="align-self-center text-center">
                                    <vue-feather type="list" class="font-success"></vue-feather>
                                </div>
                                <div class="media-body">
                                    <span class="m-0">Total KYTs</span>
                                    <h4 class="mb-0 counter">{{ summary?.totals?.kyts || 0 }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-3 col-sm-6">
                    <div class="card o-hidden">
                        <div class="bg-info b-r-4 card-body">
                            <div class="media static-top-widget">
                                <div class="align-self-center text-center">
                                    <vue-feather type="layers" class="font-info"></vue-feather>
                                </div>
                                <div class="media-body">
                                    <span class="m-0">Sub Categories</span>
                                    <h4 class="mb-0 counter">{{ summary?.totals?.subCategories || 0 }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-md-3 col-sm-6">
                    <div class="card o-hidden">
                        <div class="bg-warning b-r-4 card-body">
                            <div class="media static-top-widget">
                                <div class="align-self-center text-center">
                                    <vue-feather type="briefcase" class="font-warning"></vue-feather>
                                </div>
                                <div class="media-body">
                                    <span class="m-0">Assignments</span>
                                    <h4 class="mb-0 counter">{{ summary?.totals?.assignments || 0 }}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Charts Row -->
            <div class="row">
                <!-- Status Breakdown -->
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header">
                            <h5>Status Breakdown</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="summary?.breakdowns?.status && summary.breakdowns.status.length > 0" class="status-chart">
                                <div 
                                    v-for="status in summary.breakdowns.status" 
                                    :key="status._id"
                                    class="status-item d-flex justify-content-between align-items-center mb-3"
                                >
                                    <div class="d-flex align-items-center">
                                        <div 
                                            class="status-indicator me-3"
                                            :style="{ backgroundColor: getStatusColor(status._id) }"
                                        ></div>
                                        <span class="text-capitalize">{{ status._id }}</span>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <span class="badge badge-light me-2">{{ status.count }}</span>
                                        <small class="text-muted">
                                            {{ calculatePercentage(status.count, summary?.totals?.logs || 0) }}%
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <KytChainEmptyState 
                                v-else
                                icon="pie-chart"
                                title="No status data"
                                description="Status breakdown will appear here when data is available"
                                :show-button="false"
                            />
                        </div>
                    </div>
                </div>

                <!-- Priority Breakdown -->
                <div class="col-md-6">
                    <div class="card">
                        <div class="card-header">
                            <h5>Priority Breakdown</h5>
                        </div>
                        <div class="card-body">
                            <div v-if="summary?.breakdowns?.priority && summary.breakdowns.priority.length > 0" class="priority-chart">
                                <div 
                                    v-for="priority in summary.breakdowns.priority" 
                                    :key="priority._id"
                                    class="priority-item d-flex justify-content-between align-items-center mb-3"
                                >
                                    <div class="d-flex align-items-center">
                                        <div 
                                            class="priority-indicator me-3"
                                            :style="{ backgroundColor: getPriorityColor(priority._id) }"
                                        ></div>
                                        <span class="text-capitalize">{{ priority._id }}</span>
                                    </div>
                                    <div class="d-flex align-items-center">
                                        <span class="badge badge-light me-2">{{ priority.count }}</span>
                                        <small class="text-muted">
                                            {{ calculatePercentage(priority.count, summary?.totals?.logs || 0) }}%
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <KytChainEmptyState 
                                v-else
                                icon="bar-chart-2"
                                title="No priority data"
                                description="Priority breakdown will appear here when data is available"
                                :show-button="false"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Completion Overview -->
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h5>Completion Overview</h5>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-6">
                                    <h6>Average Completion Rate</h6>
                                    <div class="progress mb-3" style="height: 30px;">
                                        <div 
                                            class="progress-bar" 
                                            :class="getProgressBarClass(summary?.completion?.averageCompletion || 0)"
                                            :style="{ width: (summary?.completion?.averageCompletion || 0) + '%' }"
                                        >
                                            {{ summary?.completion?.averageCompletion || 0 }}%
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <h6>Total Completed</h6>
                                    <div class="d-flex align-items-center">
                                        <h3 class="text-success mb-0 me-3">{{ summary?.completion?.totalCompleted || 0 }}</h3>
                                        <small class="text-muted">
                                            out of {{ summary?.totals?.logs || 0 }} total logs
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Quick Actions -->
            <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-header">
                            <h5>Quick Actions</h5>
                        </div>
                        <div class="card-body">
                            <div class="row">
                                <div class="col-md-3 col-sm-6 mb-3">
                                    <button @click="navigateToLogs" class="btn btn-primary btn-lg w-100">
                                        <vue-feather type="database" class="me-2"></vue-feather>
                                        View All Logs
                                    </button>
                                </div>
                                <div class="col-md-3 col-sm-6 mb-3">
                                    <button @click="navigateToOpenLogs" class="btn btn-warning btn-lg w-100">
                                        <vue-feather type="clock" class="me-2"></vue-feather>
                                        Open Logs
                                    </button>
                                </div>
                                <div class="col-md-3 col-sm-6 mb-3">
                                    <button @click="navigateToHighPriority" class="btn btn-danger btn-lg w-100">
                                        <vue-feather type="alert-triangle" class="me-2"></vue-feather>
                                        High Priority
                                    </button>
                                </div>
                                <div class="col-md-3 col-sm-6 mb-3">
                                    <button @click="refreshDashboard" class="btn btn-success btn-lg w-100">
                                        <vue-feather type="refresh-cw" class="me-2"></vue-feather>
                                        Refresh Data
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import { kytChainService } from '@/services/kytChainService';
import type { ChainSummaryResponse } from '@/types/kyt-chain';
import KytChainLoading from '@/components/kyt-chain/KytChainLoading.vue';
import KytChainEmptyState from '@/components/kyt-chain/KytChainEmptyState.vue';

const router = useRouter();
const toast = useToast();

// Reactive data
const loading = ref(false);
const summary = ref<ChainSummaryResponse | null>(null);

// Methods
const fetchSummary = async () => {
    loading.value = true;
    try {
        const response = await kytChainService.getChainSummary();
        if (response.success) {
            summary.value = response.data;
        } else {
            toast.error(response.message || 'Failed to fetch summary');
        }
    } catch (error) {
        console.error('Error fetching summary:', error);
        toast.error('Error fetching dashboard data');
    } finally {
        loading.value = false;
    }
};

const refreshDashboard = () => {
    fetchSummary();
    toast.success('Dashboard refreshed');
};

const navigateToLogs = () => {
    router.push('/kyt-chain/logs');
};

const navigateToOpenLogs = () => {
    router.push('/kyt-chain/logs?status=open');
};

const navigateToHighPriority = () => {
    router.push('/kyt-chain/logs?priority=high');
};

const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
        case 'open': return '#ffc107';
        case 'partial': return '#17a2b8';
        case 'closed': return '#28a745';
        default: return '#6c757d';
    }
};

const getPriorityColor = (priority: string): string => {
    switch (priority.toLowerCase()) {
        case 'low': return '#6c757d';
        case 'medium': return '#17a2b8';
        case 'high': return '#ffc107';
        case 'critical': return '#dc3545';
        default: return '#6c757d';
    }
};

const getProgressBarClass = (percentage: number): string => {
    if (percentage < 30) return 'bg-danger';
    if (percentage < 70) return 'bg-warning';
    return 'bg-success';
};

const calculatePercentage = (count: number, total: number): number => {
    if (total === 0) return 0;
    return Math.round((count / total) * 100);
};

// Lifecycle
onMounted(() => {
    fetchSummary();
});
</script>

<style scoped>
.static-top-widget {
    padding: 20px;
}

.static-top-widget .media-body h4 {
    font-weight: 600;
    color: white;
    font-size: 2rem;
}

.static-top-widget .media-body span {
    color: rgba(255, 255, 255, 0.8);
    font-size: 14px;
}

.status-indicator,
.priority-indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
}

.status-item,
.priority-item {
    padding: 0.5rem;
    border-radius: 5px;
    transition: background-color 0.2s ease;
}

.status-item:hover,
.priority-item:hover {
    background-color: #f8f9fa;
}

.card {
    box-shadow: 0 0 20px rgba(89, 102, 122, 0.1);
    border: 1px solid #e4e7ed;
}

.btn-lg {
    padding: 0.75rem 1.25rem;
    font-size: 1rem;
    border-radius: 0.5rem;
}

.progress {
    border-radius: 10px;
}

.progress-bar {
    border-radius: 10px;
    transition: width 0.3s ease;
}

.counter {
    animation: countUp 0.5s ease-out;
}

@keyframes countUp {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
